import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { trackAnalyticsEvent } from '~/common/analytics/analyticsClient';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageHeader as EdsPageHeader } from '~/eds/src/modules/PageHeader/PageHeader';
import { AskAnythingButton } from '~/features/ask-anything/ask-anything-button/AskAnythingButton';
import { NotificationTray } from '~/features/notifications/NotificationTray';
import { useCandu } from '~/features/onboarding/Candu/useCandu';
import { CLMEvolutionFeedbackNavButton } from '~/features/workflows-clm-evolution/feedback/CLMEvolutionFeedbackNavButton';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import { useSearchModal } from '~/hooks/useSearchModal';
import { slices } from '~/redux/slices';
import { selectPageBanner } from '~/redux/slices/pages';
import search from '~/redux/slices/search';
import { RootState } from '~/redux/types';
import { useRouting } from '~/routing/useRouting';
import { logout } from '~/utils/logout';
import { NAV_PERMISSIONS, useNavlinks } from '~/utils/navLinks';

import { getSanaAcademyUrl } from './getSanaAcademyUrl';
import { SidebarNavOptInToggle } from './SidebarNavOptInToggle';
import { useSidebarNavPreference } from './SidebarNavPreferenceContext';
import { useGetWorkdayUpcApps } from './useGetWorkdayUpcApps';

const LEGACY_EVISORT_ACADEMY_URL = 'https://academy.evisort.com/';

export const AppPageNavbar = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const enableLightTheme = useFlag(FlagType.NavigationBarLightTheme);
  const { location, navigate } = useRouting();

  const [searchModal, setModal] = useSearchModal();

  const { pathname } = location;

  // initialize candu once the page is loaded (consider moving it further upstream, but after the user has logged in)
  useCandu(user);

  const banner = useSelector((state: RootState) =>
    selectPageBanner(state, pathname),
  );

  const hasAskAnything = useFlag(FlagType.AskAnything);
  // For details on CLM Evolution Phase 1 project, see Jira epic: https://jira2.workday.com/browse/EKP-40675
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  // Same args as useNavlinks's internal call, deduped by the query cache.
  const { checkPermission, isLoading: isPermissionsLoading } = usePermissions({
    permissions: NAV_PERMISSIONS,
  });
  const isSidebarNavAvailable = useFlag(FlagType.SidebarNav);
  const { isOptedIn } = useSidebarNavPreference();

  const enableCandu = useFlag(FlagType.CanduScript);
  const enableSanaAcademy = useFlag(FlagType.EnableSanaAcademyCenter);

  const evisortAcademyUrl = useMemo(() => {
    if (!enableSanaAcademy) return LEGACY_EVISORT_ACADEMY_URL;
    const sanaUrl = getSanaAcademyUrl();
    if (sanaUrl) return sanaUrl;
    console.warn("The cell doesn't support sana integration for now.");
    return LEGACY_EVISORT_ACADEMY_URL;
  }, [enableSanaAcademy]);

  const onNavLinkClick = useCallback(
    (navPathname: string) => {
      // TODO: the relevant domain pages should own the state cleanup logic
      dispatch(search.actions.reset());
      dispatch(slices.dashboardV2.actions.reset());
      dispatch(slices.searchV3.actions.reset());
      navigate(navPathname);
    },
    [dispatch, navigate],
  );

  // Calls hooks internally, so it can't be wrapped in useMemo.
  const links = useNavlinks(onNavLinkClick);

  const help = {
    actions: [
      {
        label: intl.formatMessage({
          id: 'PageHeader.knowledgeCenter.',
          defaultMessage: 'Knowledge Center',
          description: 'Label for the knowledge center menu action',
        }),
        href:
          getEnvVariable('ZENDESK_AUTH_ENTRY_URL') ||
          'https://support.evisort.com/hc/en-us',
        value: 'knowledge-center',
      },

      {
        label: intl.formatMessage({
          id: 'PageHeader.evisortAcademy',
          defaultMessage: 'Evisort Academy',
          description: 'Label for the Evisort Academy menu action',
        }),
        href: evisortAcademyUrl,
        value: 'evisort-academy',
      },
    ],
  };

  const profile = useMemo(() => {
    const settings = [];

    settings.push({
      label: intl.formatMessage({
        id: 'PageHeader.profileSettings',
        defaultMessage: 'Settings',
        description: 'Label for the Settings menu action',
      }),
      pathname: '/settings',
      value: 'profileSettings',
    });

    if (checkPermission('documents', 'view')) {
      settings.push({
        label: intl.formatMessage({
          id: 'PageHeader.deletedItems',
          defaultMessage: 'My Deleted Items',
          description: 'Label for the My Deleted Items menu action',
        }),
        pathname: '/my-deleted-items',
        value: 'myDeletedItems',
      });
    }

    settings.push({
      label: intl.formatMessage({
        id: 'PageHeader.logOut',
        defaultMessage: 'Log out',
        description: 'Label for the Log out menu action',
      }),
      onClick: () => logout(user),
      value: 'logOut',
    });

    return { settings, user };
  }, [user, intl, checkPermission]);

  const onSearchClick = () => {
    trackAnalyticsEvent('selectNavigation', {
      name: 'Quick Search',
    });
    setModal();
  };

  const { apps, isWorkdayUser } = useGetWorkdayUpcApps();
  const hasAskAnythingButton =
    hasAskAnything && checkPermission('conversational_ai', 'edit');
  const pageHeaderTheme = isWorkdayUser || enableLightTheme ? 'light' : 'dark';

  const showSidebarNavOptIn = isSidebarNavAvailable && !isOptedIn;
  const hasPreviewActions = showSidebarNavOptIn || hasClmEvolutionPhase1;

  let ctaButton: ReactNode;
  if (hasPreviewActions) {
    ctaButton = (
      <Layout align="center" flex="auto" spacing={2}>
        {/* Both layers grow so the preview actions centre in the gap the
            header's `space-between` leaves after the nav links, instead of
            bunching up against Ask AI. */}
        <Layout align="center" flex="auto" justify="center" spacing={2}>
          {showSidebarNavOptIn && (
            <SidebarNavOptInToggle theme={pageHeaderTheme} />
          )}
          {hasClmEvolutionPhase1 && (
            <CLMEvolutionFeedbackNavButton theme={pageHeaderTheme} />
          )}
        </Layout>
        {hasAskAnythingButton ? <AskAnythingButton /> : null}
      </Layout>
    );
  } else if (hasAskAnythingButton) {
    ctaButton = <AskAnythingButton />;
  }

  return (
    <>
      <EdsPageHeader
        apps={isWorkdayUser ? apps : []}
        ctaButton={ctaButton}
        isLoading={isPermissionsLoading}
        activePathname={pathname}
        banner={banner}
        help={enableCandu ? { actions: [] } : help}
        links={links}
        preSearchAction={
          <NotificationTray appearance="nav-pill" theme={pageHeaderTheme} />
        }
        profile={profile}
        theme={pageHeaderTheme}
        onSearchClick={onSearchClick}
      />

      {searchModal}
    </>
  );
};
